export const en = {
  nav: {
    model: "Our model",
    invest: "Invest",
    simulator: "Simulator",
    risks: "Risks & compliance",
    faq: "FAQ",
    login: "Log in",
    signup: "Create account",
    dashboard: "Investor area",
    logout: "Log out",
    admin: "Admin",
  },
  hero: {
    eyebrow: "Sovereign GPU infrastructure · Europe",
    title: "Invest in sovereign GPU infrastructure from €1,000",
    subtitle:
      "Access GPU clusters operated for regulated players — healthcare, insurance, medical, sovereign AI — through dedicated SPVs, long-term contracts and operational transparency.",
    cta_primary: "Run my simulation",
    cta_secondary: "Access documentation",
    proofs: [
      { k: "Real assets", v: "H100 / A100 / H200 GPUs" },
      { k: "Contracts", v: "5-year regulated clients" },
      { k: "Transparency", v: "Monthly reporting + telemetry" },
    ],
  },
  warning: {
    short:
      "Target yield is not guaranteed. Capital not guaranteed, risk of partial or total loss. Coupons capitalised semi-annually on internal InvestGPU account, liquidity only at contract maturity or via secondary marketplace transfer.",
  },
  home: {
    why_now_title: "Why now",
    why_now_body:
      "Europe lacks sovereign GPU capacity for regulated players. We fund, operate and capitalise revenues generated by EU-based clusters under 5-year framework contracts as semi-annual coupons.",
    sovereignty_title: "Why sovereignty matters",
    sovereignty_body:
      "Regulated players (healthcare, insurance, AI under the AI Act) need EU-located, traceable, European-operated infrastructure. This creates structural demand.",
    revenue_title: "How revenues are generated",
    revenue_body:
      "Each SPV owns a GPU cluster amortized over 36 months, hosted in a partner datacenter, and leased to one or several regulated clients under contract. Net rents are paid every six months as coupons credited to your InvestGPU capitalisation account.",
    industries_title: "Target industries",
  },
  model: {
    title: "Our economic model",
    subtitle: "From legal vehicle to investor capitalisation account.",
    steps: [
      { n: "01", t: "Dedicated SPV", d: "One vehicle per vintage / client, to ring-fence risks." },
      { n: "02", t: "GPU acquisition", d: "Hardware selected (H100, A100, H200) for target use." },
      { n: "03", t: "EU hosting", d: "Tier III+ partner datacenter in France or EU." },
      { n: "04", t: "Long-term leasing", d: "5-year contracts with regulated clients. Occupancy piloted." },
      { n: "05", t: "Revenues", d: "Recurring GPU rents, adjusted for energy/usage." },
      { n: "06", t: "Semi-annual coupons", d: "Twice-a-year coupons credited to your internal InvestGPU capitalisation account, ring-fenced for re-investment." },
    ],
    do: ["Fund GPU assets", "Operate monthly reporting", "Ring-fence via SPV", "Capitalise via semi-annual coupons"],
    dont: ["Promise guaranteed returns", "Offer daily liquidity", "Mix assets across SPVs", "Speculate on short resale"],
  },
  invest: {
    title: "Invest through an SPV",
    subtitle: "Minimum ticket €1,000 — 3-year horizon — 12%/y target yield, capitalised through semi-annual coupons.",
    scenarios_title: "Return scenarios",
    scenarios: [
      { key: "prudent", name: "Prudent", yield: "8%", body: "~70% occupancy, degraded market, partial renegotiation." },
      { key: "central", name: "Central", yield: "12%", body: "~85% occupancy, nominal contracts, 36-month amortization." },
      { key: "ambitious", name: "Ambitious", yield: "15%", body: ">92% occupancy, tight GPU demand, premium pricing." },
    ],
    invest_cta: "Subscribe",
    min: "Min ticket",
    horizon: "Horizon",
    target: "Target yield",
    raised: "Raised",
    total: "Target",
  },
  simulator: {
    title: "Investment simulator",
    subtitle: "Informative projection based on a scenario. Coupons paid semi-annually. Non-contractual.",
    amount: "Amount invested",
    horizon: "Duration (months)",
    scenario: "Return scenario",
    projected_return: "Estimated total return",
    monthly: "Estimated semi-annual coupon",
    annual: "Target annual yield",
    cta: "Create my investor account",
  },
  risks: {
    title: "Risks & compliance",
    subtitle: "Understand before you invest.",
    items: [
      { t: "Capital loss risk", d: "Capital is not guaranteed. Partial or total loss remains possible." },
      { t: "Technical risk", d: "Hardware failure, datacenter unavailability, GPU obsolescence." },
      { t: "Occupancy risk", d: "Drop in GPU rental rate, rent renegotiation." },
      { t: "Counterparty risk", d: "Default of a main tenant client." },
      { t: "Regulatory risk", d: "AI Act, DORA, datacenter regulation changes." },
      { t: "Liquidity risk", d: "Capitalised coupons become withdrawable only at contract maturity or via a framed transfer on the InvestGPU secondary marketplace. No early exit guaranteed." },
    ],
  },
  faq: {
    title: "Frequent questions",
    items: [
      { q: "What is the minimum ticket?", a: "€1,000 per SPV, potentially increased after profile validation." },
      { q: "What is the lock-up period?", a: "Target horizon is 36 months. No exit is guaranteed before maturity." },
      { q: "Is the return guaranteed?", a: "No. We communicate a target return in the central scenario only." },
      { q: "How and when am I paid?", a: "Net SPV revenues are paid as semi-annual coupons (twice a year, post-waterfall) credited to your internal InvestGPU capitalisation account." },
      { q: "What is the InvestGPU capitalisation account?", a: "A secured internal account, unique per investor, that receives semi-annual coupons. The balance is exclusively allocated to re-investment in another InvestGPU SPV. It is not withdrawable in cash before contract maturity." },
      { q: "When does the balance become liquid?", a: "Your capitalisation balance becomes withdrawable only (i) at SPV contract maturity (typically 36 months) or (ii) upon a framed transfer of your shares on the InvestGPU secondary marketplace. No early exit is guaranteed." },
      { q: "How am I informed?", a: "Operational reporting (telemetry, SPV KPIs, notable events) is published monthly in your investor area. Coupon payouts themselves are semi-annual." },
      { q: "Am I eligible to invest?", a: "A qualification questionnaire is mandatory before any subscription." },
      { q: "What taxation applies?", a: "Taxation depends on your country of residence and status." },
    ],
  },
  auth: {
    login_title: "Log in",
    signup_title: "Create your investor area",
    email: "Email",
    password: "Password",
    name: "Full name",
    submit_login: "Log in",
    submit_signup: "Create account",
    google: "Continue with Google",
    or: "or",
    have_account: "Already registered?",
    no_account: "Not registered yet?",
    terms: "I accept the Terms of Service and GDPR policy.",
  },
  qualification: {
    title: "Investor qualification",
    subtitle: "Mandatory questionnaire before any subscription.",
    country: "Country of residence",
    investor_type: "Investor type",
    experience: "Years of investment experience",
    net_worth: "Financial wealth range",
    risk_tolerance: "Risk tolerance",
    horizon_accepted: "I accept a 36-month lock-up period.",
    loss_accepted: "I understand I may lose part or all of my invested capital.",
    liquidity_accepted: "I accept that liquidity is limited or non-existent before maturity.",
    pep_status: "I am a Politically Exposed Person (PEP).",
    source_of_funds: "Source of funds",
    submit: "Validate my qualification",
  },
  dashboard: {
    welcome: "Hello",
    value_invested: "Committed capital",
    total_distributed: "Capitalisation account balance",
    target_yield: "Target yield",
    next_distribution: "Next estimated semi-annual coupon",
    active_spvs: "Active SPVs",
    gpu_live: "GPU Activity · Live",
    occupancy: "Occupancy",
    uptime: "Uptime",
    power: "Power",
    gpus_active: "Active GPUs",
    daily_revenue: "Daily revenue",
    co2_saved: "CO₂ saved (est.)",
    performance: "Performance per SPV",
    documents: "Documents",
    subscribe_new: "Subscribe to a new SPV",
    coupon_account_title: "InvestGPU Capitalisation Account",
    coupon_account_desc: "Coupons are credited every 6 months to this internal account. The balance is exclusively allocated to re-investment in another InvestGPU SPV. Cash withdrawal is only available at SPV contract maturity or upon a framed transfer on the secondary marketplace.",
    coupon_balance: "Capitalised balance",
    coupon_next: "Next estimated coupon",
    coupon_next_date: "Estimated date",
    coupon_locked_label: "Locked until contract maturity",
    coupon_action_reinvest: "Re-invest",
    coupon_action_marketplace: "View marketplace (coming soon)",
  },
  footer: {
    rights: "All rights reserved",
    legal: "Legal notices",
    privacy: "Privacy policy",
    disclaimer:
      "InvestGPU is an investment platform in GPU infrastructure operated via dedicated SPVs. Offerings are not state-guaranteed. Capital carries a risk of partial or total loss. Access is subject to file validation. Some features may be limited by jurisdiction. Past or simulated performance does not prejudge future performance.",
  },
};
